import React from "react";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import type { AnomalyWeekly, OverdueInvoice, FailureRecord } from "../../../types/kpiDashboard";

interface Props {
  currentAnomaly: AnomalyWeekly | null;
  prevAnomaly:    AnomalyWeekly | null;
  overdueInvoices: OverdueInvoice[];
  failures:        FailureRecord[];
}

const AlertRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-content-between align-items-center py-2"
       style={{ borderBottom: "1px solid #f1f5f9" }}>
    <span className="text-sm text-color-secondary">{label}</span>
    <span className="text-sm font-bold text-color">{value}</span>
  </div>
);

const AlertSummaryCards: React.FC<Props> = ({
  currentAnomaly, prevAnomaly, overdueInvoices, failures,
}) => {
  const totalOverdue = overdueInvoices.reduce((sum, inv) => sum + inv.amount, 0);
  const maxDaysOverdue = overdueInvoices[0]?.daysOverdue ?? 0;
  const worstDebtor = overdueInvoices[0]?.company ?? "—";

  const anomalyTotal   = currentAnomaly?.total ?? 0;
  const anomalyPartner = currentAnomaly?.partnerLeer ?? 0;
  const prevTotal      = prevAnomaly?.total ?? 0;

  const anomalySeverity  = anomalyTotal >= 25 ? "danger" : anomalyTotal >= 10 ? "warning" : "success";
  const partnerSeverity  = anomalyPartner >= 30 ? "danger" : anomalyPartner >= 15 ? "warning" : "success";
  const invoiceSeverity  = totalOverdue >= 10000 ? "danger" : totalOverdue >= 5000 ? "warning" : "success";
  const failureSeverity  = failures.length > 0 ? "danger" : "success";

  return (
    <div className="grid mt-0">
      {/* CDR Anomalies */}
      <div className="col-12 md:col-4">
        <Card title={`CDR Anomalies — ${currentAnomaly?.week ?? "—"}`}
              style={{ borderLeft: `4px solid ${anomalySeverity === "danger" ? "#ef4444" : anomalySeverity === "warning" ? "#f59e0b" : "#22c55e"}` }}>
          <AlertRow label="GENERAL_PARTNER_LEER"
            value={<Tag severity={partnerSeverity} value={`${anomalyPartner}`} />} />
          <AlertRow label="CHARGING_NO_CONSUMPTION"
            value={currentAnomaly?.noConsumption ?? 0} />
          <AlertRow label="METER_STOP_SMALLER_START"
            value={currentAnomaly?.meterStop ?? 0} />
          <AlertRow label="Total anomalies"
            value={
              <span>
                <Tag severity={anomalySeverity} value={`${anomalyTotal}`} />
                <span className="text-xs text-color-secondary ml-2">(prev: {prevTotal})</span>
              </span>
            } />
        </Card>
      </div>

      {/* Overdue Invoices */}
      <div className="col-12 md:col-4">
        <Card title="EasyBill — Overdue Summary"
              style={{ borderLeft: `4px solid ${invoiceSeverity === "danger" ? "#ef4444" : invoiceSeverity === "warning" ? "#f59e0b" : "#22c55e"}` }}>
          <AlertRow label="Total overdue amount"
            value={<Tag severity={invoiceSeverity} value={`€${totalOverdue.toLocaleString("de-DE", { minimumFractionDigits: 2 })}`} />} />
          <AlertRow label="Number of invoices"
            value={`${overdueInvoices.length} invoices`} />
          <AlertRow label="Longest overdue"
            value={<Tag severity="danger" value={`${maxDaysOverdue} days`} />} />
          <AlertRow label="Worst debtor"
            value={<span className="text-xs">{worstDebtor}</span>} />
        </Card>
      </div>

      {/* Ground Failures */}
      <div className="col-12 md:col-4">
        <Card title="GROUND_FAILURE Incidents"
              style={{ borderLeft: `4px solid ${failureSeverity === "danger" ? "#ef4444" : "#22c55e"}` }}>
          {failures.length === 0 ? (
            <AlertRow label="Status" value={<Tag severity="success" value="No failures recorded" />} />
          ) : (
            failures.slice(0, 4).map((f, i) => (
              <AlertRow
                key={i}
                label={`${f.station} (${f.year}-${String(f.month).padStart(2, "0")})`}
                value={<Tag severity="danger" value={f.duration} />}
              />
            ))
          )}
          <AlertRow label="Total incidents"
            value={<Tag severity={failureSeverity} value={`${failures.length}`} />} />
        </Card>
      </div>
    </div>
  );
};

export default AlertSummaryCards;
